/**
 * Contratos de dominio compartidos entre la API, la web y la extensión.
 * Fuente de verdad de los tipos del SDD SaaS §10.
 *
 * IMPORTANTE: este módulo NO importa nada de Node, del navegador ni de
 * frameworks. Se consume en los tres artefactos.
 */

/** Estados del ciclo de vida de una grabación (SDD §8.4). */
export type RecordingStatus =
  | 'recording' // la extensión está grabando y subiendo trozos
  | 'captured' // `complete` recibido; a la espera de ensamblar
  | 'queued'
  | 'processing' // transcribiendo
  | 'merging'
  | 'summarizing'
  | 'completed'
  | 'failed'
  | 'retenida' // sin minutos en el plan; el audio espera en `held/`
  | 'held' // detenida por control de costos del operador
  | 'aborted'

/** Estados terminales: no cambian solos. */
export const TERMINAL_STATUSES: readonly RecordingStatus[] = [
  'completed',
  'failed',
  'aborted'
] as const

/** Estados en los que la web hace polling (hay trabajo en curso). */
export const ACTIVE_STATUSES: readonly RecordingStatus[] = [
  'recording',
  'captured',
  'queued',
  'processing',
  'merging',
  'summarizing'
] as const

/** Fallos por la duración real o el audio vencido: reintentar no los arregla. */
export const NO_RETRY_ERROR_CODES: readonly string[] = [
  'demasiado_larga',
  'duracion_no_coincide',
  'retenida_vencida'
]

/**
 * Motivo en español de una grabación fallida o retenida (códigos del pipeline).
 * Lo usan la web y el correo de «no pudimos procesar».
 */
export function recordingErrorLabel(code: string | null): string {
  switch (code) {
    case 'quota_exceeded':
      return 'Te quedaste sin horas este mes. Mejora tu plan y se procesa sola.'
    case 'sin_audio':
      return 'No recibimos audio de esta grabación.'
    case 'transcripcion_fallida':
      return 'No pudimos transcribir el audio.'
    case 'import_signature_mismatch':
      return 'El archivo no parece ser un audio válido.'
    case 'job_agotado':
    case 'atascada':
      return 'El procesamiento tardó demasiado.'
    case 'retenida_vencida':
      return 'Esperó 7 días sin poder procesarse y el audio se borró. Si guardaste la copia local, impórtala.'
    case 'demasiado_larga':
      return 'La grabación supera la duración máxima de tu plan.'
    case 'duracion_no_coincide':
      return 'El audio dura más de lo que alcanzan tus horas. Mejora tu plan e impórtalo de nuevo.'
    case 'ensamblado_fallido':
    case 'sin_trozos':
    case 'sin_trozo_cero':
      return 'Faltaron partes del audio y no se pudo armar la grabación.'
    case 'trozo_demasiado_grande':
    case 'total_demasiado_grande':
      return 'El audio es más grande de lo permitido.'
    default:
      return 'Algo falló al procesar esta grabación.'
  }
}

/** Modo de la reunión: online (mic + pestaña) o presencial (solo mic). */
export type RecordingMode = 'online' | 'presencial'

/** Origen de la grabación. */
export type RecordingOrigin = 'extension' | 'import'

/** Plataforma de la reunión, cuando se conoce. */
export type Platform = 'meet' | 'teams' | 'zoom' | 'otro'

/** Pista de audio: micrófono ("Yo"), pestaña ("los demás") o única (presencial / importación). */
export type TrackLabel = 'mic' | 'others' | 'single'

/** Estado de una pista dentro del pipeline (SDD §8.4). */
export type TrackStatus =
  'pending' | 'assembled' | 'submitting' | 'transcribing' | 'transcribed' | 'failed' | 'skipped'

/** Origen de un hablante: pista del micrófono o diarización. */
export type SpeakerOrigin = 'mic' | 'diar'

/** Estado de una versión de notas. */
export type SummaryStatus = 'generating' | 'ok' | 'rechazado' | 'failed' | 'editada'

/** Identificadores de las tres plantillas fijas de v1. */
export type TemplateId = 'reunion_general' | 'entrevista' | 'reunion_cliente' | 'personalizada'
/** Plantillas de eco (las que se pueden elegir sin crear una propia). */
export type StandardTemplateId = Exclude<TemplateId, 'personalizada'>

/** Sección de una plantilla personalizada: título y qué debe ir en ella. */
export interface CustomSection {
  titulo: string
  instruccion: string
}

/** Plantilla de notas definida por el usuario (de la organización). */
export interface CustomTemplate {
  id: string
  nombre: string
  descripcion: string
  secciones: CustomSection[]
  creadoEn: string
  actualizadoEn: string
}

/** Tema a seguir: palabras clave que se marcan en cada reunión (p. ej. «Competencia»). */
export interface TopicTracker {
  id: string
  nombre: string
  terminos: string[]
  creadoEn: string
}

/** Webhook saliente (Zapier, Make, n8n…): recibe las notas cuando están listas. */
export interface Webhook {
  id: string
  url: string
  /** Secreto para verificar la firma `eco-signature` (HMAC-SHA256). */
  secreto: string
  incluirTranscripcion: boolean
  activo: boolean
  fallosSeguidos: number
  /** Último código HTTP recibido (0 = sin respuesta). */
  ultimoEstado: number | null
  ultimoEnvioEn: string | null
  creadoEn: string
}

/** Planes de v1 (Equipo llega en v1.x pero el tipo ya existe). */
export type PlanId = 'free' | 'personal' | 'equipo'

/** Movimientos del libro mayor de minutos (SDD §14.3). */
export type UsageType = 'hold' | 'processing' | 'refund' | 'credit' | 'adjust' | 'summary'

/** Rol dentro de una organización. */
export type MembershipRole = 'owner' | 'admin' | 'member'

/** Un usuario tal como lo ve la API. */
export interface User {
  id: string
  email: string
  nombre: string | null
  locale: string
  esAdmin: boolean
  consentimientoAceptadoEn: string | null // ISO 8601
  /** Versión de los términos aceptada (TERMINOS_VERSION) y cuándo. */
  terminosVersion: string | null
  terminosAceptadosEn: string | null
  creadoEn: string
  /** Correos que el usuario quiere recibir. */
  notificaciones: UserNotifications
  /** Plantilla de las notas automáticas. */
  plantillaDefecto: StandardTemplateId
}

export interface UserNotifications {
  notas_listas: boolean
  cuota: boolean
  /** Correo de los lunes con las reuniones, tareas y decisiones de la semana. */
  resumen_semanal: boolean
  /** Consejos y recordatorios de eco: todos los correos de marketing (activación, hábito…). */
  consejos: boolean
}

export interface Organization {
  id: string
  nombre: string
  tipo: 'personal' | 'equipo'
  zonaHoraria: string
  cicloAncla: string // fecha YYYY-MM-DD
  retencionDias: number | null
  compartirHabilitado: boolean
  creadoEn: string
  /** Palabras que el proveedor de transcripción debe reconocer (nombres, siglas, jerga). */
  vocabulario?: string[]
  /** Si se acortó la retención: no se borra nada antes de esta fecha (aviso previo). */
  retencionDesde?: string | null
}

export interface Project {
  id: string
  orgId: string
  nombre: string
  descripcion: string | null
  creadoEn: string
  /** Carpeta del equipo: todos los miembros ven sus reuniones (plan Equipo). */
  compartido: boolean
}

/** Quién ve una reunión dentro de su organización (plan Equipo; en la personal da igual). */
export type Visibility = 'privada' | 'equipo'

/** Invitación pendiente a una organización de equipo. */
export interface Invitation {
  id: string
  orgId: string
  email: string
  rol: Exclude<MembershipRole, 'owner'>
  invitadoPor: string | null
  expiraEn: string
  creadoEn: string
}

/** Miembro de una organización (Ajustes → Equipo). */
export interface OrgMember {
  userId: string
  email: string
  nombre: string | null
  rol: MembershipRole
  desde: string
}

/** Advertencias que el pipeline anota sobre una grabación. */
export type RecordingWarning =
  | 'trozos_perdidos'
  | 'others_vacia'
  | 'mic_vacia'
  | 'bleed_alto'
  | 'offscreen_crash'
  | 'mic_cambiado'
  | 'finalizada_automaticamente'
  | 'pista_fallida'
  | 'truncada_por_cuota'

/** Una grabación tal como vive en la base de datos y viaja por la API. */
export interface Recording {
  id: string
  orgId: string
  projectId: string | null
  ownerUserId: string | null
  titulo: string
  descripcion: string | null
  fechaInicio: string
  duracionMs: number
  modo: RecordingMode
  origen: RecordingOrigin
  plataforma: Platform | null
  idioma: string
  estado: RecordingStatus
  offsetOthersMs: number
  consentimientoDeclaradoEn: string | null
  advertencias: RecordingWarning[]
  errorCode: string | null
  ultimaActividadEn: string | null
  completadoEn: string | null
  audioBorradoEn: string | null
  creadoEn: string
  /** Momentos marcados por el usuario durante la grabación (ms desde el inicio). */
  marcas: number[]
  /** Privada (solo su dueño) o compartida con el equipo. */
  visibilidad: Visibility
}

export interface RecordingTrack {
  recordingId: string
  label: TrackLabel
  t0Ms: number | null
  duracionMs: number | null
  duracionProveedorMs: number | null
  trozosEsperados: number | null
  trozosRecibidos: number
  estado: TrackStatus
  proveedor: string | null
  externalId: string | null
  intento: number
  /** Clave del objeto ensamblado (o del archivo importado) en R2; null hasta ensamblar. */
  objectKey: string | null
}

/** Un hablante identificado dentro de una grabación. */
export interface Speaker {
  id: number
  recordingId: string
  etiqueta: string // 'MIC' | 'SPEAKER_00' | ...
  etiquetaProveedor: string | null // 'A', '0', ... tal como vino
  nombre: string | null // editable por el usuario
  origen: SpeakerOrigin
}

/** Segmento de transcripción listo para mostrar (hablante ya resuelto a etiqueta). */
export interface TranscriptSegment {
  inicioMs: number
  finMs: number
  speaker: string // 'Yo' | 'Participante 1' | nombre real
  texto: string
  confianza?: number
  solapado?: boolean
}

/** Segmento tal como se persiste (referencia al hablante por id). */
export interface StoredTranscriptSegment {
  id: number
  recordingId: string
  speakerId: number | null
  inicioMs: number
  finMs: number
  texto: string
  confianza: number | null
  solapado: boolean
}

/** Cita anclada a la transcripción: la evidencia de un ítem de las notas. */
export interface Evidence {
  inicioMs: number
  cita: string
}

/** Tarea / compromiso extraído de las notas. */
export interface ActionItem {
  descripcion: string
  /** Etiqueta estable del hablante ('Yo' | 'Participante N') o null. */
  responsableEtiqueta: string | null
  /** Nombre dicho textualmente en la reunión, si lo hubo. */
  responsableMencionado: string | null
  fecha: string | null
  evidencia: Evidence[]
  sinEvidencia?: boolean
}

/** Uso de tokens y costo de una llamada al modelo. */
export interface ModelUsage {
  model: string
  inputTokens: number
  cacheReadTokens: number
  cacheCreationTokens: number
  outputTokens: number
  stopReason: string | null
  messageId: string | null
  costoUsd: number
}

/** Una versión de notas persistida. */
export interface Summary {
  id: string
  recordingId: string
  templateId: TemplateId
  /** Solo con templateId 'personalizada': la plantilla propia usada (null si se borró). */
  customTemplateId?: string | null
  version: number
  estado: SummaryStatus
  /** Contenido validado contra el esquema de la plantilla (ver templates.ts). */
  contenido: unknown
  modelo: string | null
  usage: ModelUsage | null
  feedback: 'up' | 'down' | null
  feedbackMotivo: string | null
  creadoEn: string
}

/** Resultado de una corrección de notas por chat. */
export interface SummaryCorrection {
  /** Versión nueva (editada) con las notas corregidas; null si no hubo cambios. */
  summary: Summary | null
  respuesta: string
  /** Términos mal transcritos que conviene agregar al vocabulario. */
  terminos: { dicho: string; correcto: string }[]
  correccionesRestantes: number
}

export interface Share {
  id: string
  recordingId: string
  alcance: 'notas' | 'notas_transcripcion'
  ocultarNombres: boolean
  expiraEn: string | null
  revocadoEn: string | null
  creadoEn: string
}

export interface BillingPeriod {
  id: string
  orgId: string
  inicio: string
  fin: string
  plan: PlanId
  minutosIncluidos: number
  asientos: number
}

export interface UsageEntry {
  id: number
  orgId: string
  recordingId: string | null
  tipo: UsageType
  minutos: number
  ocurridoEn: string
  detalle: Record<string, unknown> | null
}

export interface Subscription {
  orgId: string
  proveedor: 'paddle' | 'manual' | null
  externalId: string | null
  plan: PlanId
  asientos: number
  estado: string
  periodoInicio: string | null
  periodoFin: string | null
  cancelarEn: string | null
  /** `occurred_at` del último evento de Paddle aplicado (para descartar los atrasados). */
  ultimoEventoEn?: string | null
}

/** Resumen de uso que devuelve `GET /v1/orgs/:id/usage`. */
export interface UsageSummary {
  plan: PlanId
  periodoInicio: string
  periodoFin: string
  minutosIncluidos: number
  minutosUsados: number
  minutosRestantes: number
  grabacionesRetenidas: number
}

/** Vista de detalle: grabación + todo lo asociado. */
export interface RecordingDetail {
  recording: Recording
  tracks: RecordingTrack[]
  speakers: Speaker[]
  segments: TranscriptSegment[]
  summaries: Summary[]
  project: Project | null
  shares: Share[]
}

/** Página de resultados con cursor opaco. */
export interface Page<T> {
  items: T[]
  nextCursor: string | null
}

/** Resultado de búsqueda: una grabación con fragmentos resaltados. */
export interface SearchHit {
  recording: Pick<Recording, 'id' | 'titulo' | 'fechaInicio' | 'projectId'>
  fragmentos: string[]
  rank: number
}

/** Resultado genérico de operaciones que pueden fallar sin lanzar. */
export type Result<T, E = string> = { ok: true; value: T } | { ok: false; error: E }
